import React, { useCallback, useEffect, useState } from "react";
import { Select, Spin, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";

import IssueFormModal from "./IssueFormModal";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { createIssue } from "../../../../core/data/redux/projects/thunks";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type { Issue } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface EpicLinkProps {
  projectId: number | null | undefined;
  value: number | null | undefined;
  onChange: (epicId: number | null) => void;
  excludeId?: number | null;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

const EpicLink: React.FC<EpicLinkProps> = ({
  projectId,
  value,
  onChange,
  excludeId,
  disabled,
  size = "middle",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const [epics, setEpics] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [newEpicOpen, setNewEpicOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadEpics = useCallback(() => {
    if (!projectId || !activeCompany.company_id) return Promise.resolve<Issue[]>([]);
    setLoading(true);
    return issuesApi
      .listIssues(activeCompany.company_id, activeCompany.company_type, {
        project_id: projectId,
        type: "EPIC",
        limit: 200,
      })
      .then((data: any) => {
        const list = Array.isArray(data?.issues) ? data.issues : [];
        setEpics(list);
        return list as Issue[];
      })
      .catch(() => {
        setEpics([]);
        return [] as Issue[];
      })
      .finally(() => setLoading(false));
  }, [projectId, activeCompany.company_id, activeCompany.company_type]);

  useEffect(() => {
    let cancelled = false;
    loadEpics().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadEpics]);

  const handleQuickCreateEpic = async (payload: Record<string, any>) => {
    setCreating(true);
    try {
      const created: any = await dispatch(createIssue(payload)).unwrap();
      const newId =
        created?.issue?.id ?? created?.id ?? created?.data?.issue?.id ?? null;
      notify.success("Epic created");
      setNewEpicOpen(false);
      await loadEpics();
      if (newId) onChange(Number(newId));
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to create epic"));
      throw e;
    } finally {
      setCreating(false);
    }
  };

  const options = epics
    .filter((e) => (excludeId ? e.id !== excludeId : true))
    .map((e) => ({
      value: e.id,
      label: (
        <span className="d-inline-flex align-items-center gap-2">
          <span
            className="d-inline-block rounded-circle"
            style={{
              width: 8,
              height: 8,
              background: e.epic_color || "#8B5CF6",
            }}
          />
          <span className="text-muted small">{e.issue_key}</span>
          <span className="text-truncate" style={{ maxWidth: 220 }}>
            {e.title}
          </span>
        </span>
      ),
    }));

  return (
    <>
      <div className="d-flex align-items-center gap-1">
        <Select
          style={{ flex: 1, height: 36 }}
          allowClear
          showSearch
          placeholder="No epic"
          value={value ?? undefined}
          onChange={(v) => onChange(v ?? null)}
          disabled={disabled}
          size={size}
          loading={loading}
          options={options}
          notFoundContent={
            loading ? <Spin size="small" /> : "No epics in this project"
          }
          filterOption={(input, option) =>
            String(option?.label || "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
        <Tooltip title="Create new epic">
          <button
            type="button"
            className="btn btn-sm btn-light"
            style={{ borderRadius: 6, border: "1px solid #e2e8f0" }}
            onClick={() => setNewEpicOpen(true)}
            disabled={disabled || !projectId}
          >
            <i className="ti ti-plus" />
          </button>
        </Tooltip>
      </div>
      <IssueFormModal
        open={newEpicOpen}
        defaultType="EPIC"
        onSubmit={handleQuickCreateEpic}
        onCancel={() => setNewEpicOpen(false)}
        submitting={creating}
      />
    </>
  );
};

export default EpicLink;
